import { reportEvent } from '@/lib/monitoring'
import { ServiceError, isServiceError, unsupported } from '../errors'
import { pmsUrl, postJson } from '../http'
import { readLocal, writeLocal } from '../local-store'
import type { CatalogOption } from '../reserva/types'
import { parseMoney } from '../types'
import type { Money, StayRef } from '../types'
import { PAYMENT_POLL_MS, PAYMENT_WAIT_MS, centsToPmsPrice, isAlreadyPaid, paidTypeIdFor, sleep } from './flow'
import { fetchPendingToPaid, clearPendingToPaidCache, pmsDateToIso } from './pendingToPaid'
import type { ApiPendingToPaid } from './pendingToPaid'
import type { FiscalData, PagosService, PaymentRequest, PaymentSummary } from './types'

const orNull = (s: string | undefined | null) => (s && s.trim() !== '' ? s : null)
const zero = (currency: string): Money => ({ amount: 0, currency })

export const FISCAL_FIELD = 'fiscalData'

/** Adapta E2 al resumen de pagos. Lo que la API no devuelve (desglose, métodos distintos de tarjeta) queda vacío. */
export function toPaymentSummary(api: ApiPendingToPaid, ctx: { currency: string; cardChannel: boolean; previousPaidOnCard: boolean; channelName: string | null }): PaymentSummary {
  const c = ctx.currency
  const total = parseMoney(api.totalPrice, c) ?? zero(c)
  const pending = parseMoney(api.pendingPrice, c) ?? zero(c)
  const advanceAmount = parseMoney(api.advancePrice, c)
  const restAmount = parseMoney(api.restPrice, c)
  const percent = api.advancePercent ? Number(api.advancePercent.replace(',', '.')) : NaN
  const savedCardId = orNull(api.validCardId)
  const methods: PaymentSummary['methods'] = []
  if (savedCardId) methods.push({ id: savedCardId, kind: 'saved_card' })
  methods.push({ id: orNull(api.paymentTypeCardId) ?? 'card', kind: 'card' })
  return {
    total,
    pending,
    paid: { amount: Math.max(0, total.amount - pending.amount), currency: c },
    lines: [],
    methods,
    savedCard: savedCardId
      ? { id: savedCardId, last4: orNull(api.cardNumber)?.replace(/\D/g, '').slice(-4) ?? null, holder: orNull(api.cardName), expiry: orNull(api.cardExpirationDate), needsCvv: api.emptyCVV !== false }
      : null,
    advance: advanceAmount || api.advancePaid ? { amount: advanceAmount, percent: Number.isFinite(percent) ? percent : null, paid: Boolean(api.advancePaid), paidAt: pmsDateToIso(api.advancePaymentDate) } : null,
    rest: restAmount || api.restPaid ? { amount: restAmount, paid: Boolean(api.restPaid), paidAt: pmsDateToIso(api.restPaymentDate) } : null,
    // Como HomePayment.tsx de la app actual: sin nombre de canal no se pide validar la tarjeta del canal (fase 4, A7).
    channelValidation: { required: ctx.cardChannel && Boolean(ctx.channelName) && !ctx.previousPaidOnCard && !api.restPaid, channelName: ctx.channelName },
    freeCancel: Boolean(api.freeCancel),
    freeCancelUntil: pmsDateToIso(api.freeCancelLimitDate),
    rateConditions: orNull(api.rateConditions),
    ids: {
      standard: orNull(api.paidTypeStandardId),
      advance: orNull(api.paidTypeAdvanceId),
      rest: orNull(api.paidTypeRestId),
      bail: orNull(api.paidTypeBailId),
      paymentCard: orNull(api.paymentTypeCardId),
      paymentBizum: orNull(api.paymentTypeBizumId),
    },
  }
}

/** Respuesta de `paidreservefromapp_api` (E3, copiada de src/api/paidreservefromapp_api de la app actual). */
export interface ApiPaidReserveFromApp {
  ok: boolean
  valid?: boolean
  message?: string
  error?: string
  status?: string
  temporalPaidId?: string
  paidComplete?: boolean
  paidRedirectURL?: string
  paidState?: string
}

/** Respuesta de `checkpaymentstatefromapp_api` (E4). */
export interface ApiCheckPaymentState {
  ok: boolean
  paidState: 'pendiente' | 'completado' | 'cancelado'
}

/** Respuesta de `generatebill_guestapp` (E15). */
export interface ApiGenerateBill {
  billList?: { id: string; path: string }[]
  ok: boolean
  status?: string
  statusType?: string
}

/**
 * Cuerpo de E3, igual que PaymentForm.tsx y ChannelValidation.tsx. Con la tarjeta guardada la app actual
 * envía el CVV que muestra el formulario: el escrito si el PMS lo pide (`emptyCVV`) y, si no, el marcador
 * `'***'`. La validación del canal (ChannelValidation.tsx:142) envía siempre `cardCVV` con lo escrito, que es
 * cadena vacía cuando no se pide el CVV (fase 4, A12). Se conserva tal cual (pregunta abierta en docs/backend/pagos.md).
 */
export function toPaymentBody(stay: StayRef, request: PaymentRequest, paidTypeId: string | undefined, paymentTypeId: string | undefined) {
  const base = { price: centsToPmsPrice(request.amount.amount), reserveId: stay.id, paidTypeId, paymentTypeId }
  const { card } = request
  if (card.kind === 'new') return { ...base, cardCVV: card.cvv, cardName: card.name.trim(), cardNumber: card.number.replace(/\D/g, ''), cardExpirationDate: card.expiry }
  const cvv = card.cvv ?? (request.kind === 'channel' ? '' : '***')
  return { ...base, cardId: card.id, cardCVV: cvv }
}

/** Cuerpo de E15 (RequestInvoice.tsx): empresa sin apellido ni tipo de documento; la provincia solo si hay. */
export function toInvoiceBody(stay: StayRef, data: FiscalData) {
  const common = {
    reserveId: stay.id,
    name: data.name.trim(),
    identifier: data.identifier.trim(),
    address: data.address.trim(),
    postalCode: data.postalCode.trim(),
    countryId: data.countryId,
    city: data.city.trim(),
    ...(data.provinceId ? { provinceId: data.provinceId } : {}),
    ...(data.email.trim() ? { email: data.email.trim() } : {}),
  }
  return data.clientType === 'business' ? { ...common, enterprise: true } : { ...common, surname: data.surname.trim(), identifierTypeId: data.identifierTypeId, enterprise: false }
}

/** Un fallo de red se reintenta como en la app actual (`Network Error`); un error HTTP o no ok, no. */
const isNetworkError = (e: unknown) => isServiceError(e) && (e.code === 'offline' || (e.code === 'server' && e.status === undefined && e.message !== 'aborted'))


export const pagosReal: PagosService = {
  async getPaymentSummary(stay, lang, signal) {
    const api = await fetchPendingToPaid(stay, lang, signal)
    if (!api.ok) throw new ServiceError('server', api.error ?? api.message, api.status)
    return toPaymentSummary(api, { currency: stay.currency, cardChannel: stay.flags.cardChannel, previousPaidOnCard: stay.flags.previousPaidOnCard, channelName: stay.channel.name })
  },

  // Mismo orden que makeCardPayment (useCardPayment.tsx): E2 recién pedido → ¿ya pagado? → E3.
  async pay(stay, request, lang, signal) {
    clearPendingToPaidCache()
    const api = await fetchPendingToPaid(stay, lang, signal)
    if (!api.ok) throw new ServiceError('server', api.error ?? api.message, api.status)
    const paidTypeId = paidTypeIdFor(request.kind, api)
    if (isAlreadyPaid(paidTypeId, api)) throw new ServiceError('already_paid')
    let data: ApiPaidReserveFromApp
    try {
      data = await postJson<ApiPaidReserveFromApp>(pmsUrl(stay.serverPath, 'paidreservefromapp_api'), toPaymentBody(stay, request, paidTypeId, api.paymentTypeCardId), { auth: 'partner', ...(signal ? { signal } : {}) })
    } catch (e) {
      // HTTP distinto de 500 → "El pago ha fallado" con el texto de la tarjeta; lo demás, genérico (app actual).
      if (isServiceError(e) && e.code === 'offline') throw e
      const card = isServiceError(e) && typeof e.status === 'number' && e.status !== 500
      throw new ServiceError('payment_failed', card ? 'card' : 'generic', isServiceError(e) ? e.status : undefined)
    } finally {
      clearPendingToPaidCache()
    }
    if (!data.ok) throw new ServiceError('payment_failed', 'generic', data.status)
    if (data.paidComplete) return { status: 'completed' }
    if (data.paidRedirectURL && data.temporalPaidId) return { status: 'redirect', iframeUrl: data.paidRedirectURL, temporalPaidId: data.temporalPaidId }
    throw new ServiceError('payment_failed', 'generic', data.status)
  },

  async waitForResult(stay, temporalPaidId, { signal, timeoutMs = PAYMENT_WAIT_MS } = {}) {
    const deadline = Date.now() + timeoutMs
    for (;;) {
      if (signal?.aborted) throw new ServiceError('server', 'aborted')
      try {
        const data = await postJson<ApiCheckPaymentState>(pmsUrl(stay.serverPath, 'checkpaymentstatefromapp_api'), { reserveId: stay.id, temporalPaidId }, { auth: 'partner', ...(signal ? { signal } : {}) })
        if (!data.ok) return 'rejected'
        if (data.paidState === 'completado') {
          clearPendingToPaidCache()
          return 'completed'
        }
        if (data.paidState !== 'pendiente') return 'rejected'
      } catch (e) {
        if (!isNetworkError(e)) {
          if (isServiceError(e) && e.message === 'aborted') throw e
          return 'rejected'
        }
      }
      if (Date.now() + PAYMENT_POLL_MS > deadline) {
        reportEvent('payment_wait_timeout', { endpoint: 'checkpaymentstatefromapp_api' })
        return 'timeout'
      }
      await sleep(PAYMENT_POLL_MS, signal)
    }
  },

  async getSavedCard(stay, lang, signal) {
    const summary = await pagosReal.getPaymentSummary(stay, lang, signal)
    if (!summary.savedCard) return null
    // Sin endpoint el consentimiento no se puede registrar: queda pendiente y no habilita cargos (D37).
    return { card: { ...summary.savedCard, brand: null }, consent: { status: 'pending_confirmation', recordedAt: null, textVersion: null } }
  },
  setInStayConsent: unsupported,

  // Sin endpoint y sin datos de ejemplo en una reserva real (D36): la pantalla muestra su estado vacío u oculta la parte.
  getCharges: unsupported,
  getTouristTax: unsupported,
  notifyTaxExemption: unsupported,
  getReceipts: unsupported,
  resendReceipts: unsupported,

  getInvoiceInfo(stay) {
    return Promise.resolve({
      bills: stay.flags.showGetBills ? stay.bills : [],
      // Misma regla que `isReserveFinished` de la app actual: la fase `after` empieza a la hora de salida (D5).
      canRequest: stay.phase === 'after',
      fiscalDeadline: null,
      saved: readLocal<FiscalData>(stay.guestCode, FISCAL_FIELD),
    })
  },
  saveFiscalData(stay, data) {
    writeLocal(stay.guestCode, FISCAL_FIELD, data)
    return Promise.resolve()
  },

  async requestInvoice(stay, data) {
    writeLocal(stay.guestCode, FISCAL_FIELD, data)
    const res = await postJson<ApiGenerateBill>(pmsUrl(stay.serverPath, 'generatebill_guestapp'), toInvoiceBody(stay, data), { auth: 'partner' })
    if (!res.ok) throw new ServiceError('server', res.statusType, res.status)
    return (res.billList ?? []).map((b) => ({ id: b.id, url: b.path }))
  },

  async getProvinces(stay, countryId, signal) {
    const list = await postJson<{ id: string; name: string }[]>(pmsUrl(stay.serverPath, 'getprovincelistofcountry'), { id: countryId }, { auth: 'partner', ...(signal ? { signal } : {}) })
    return (Array.isArray(list) ? list : []).map((p): CatalogOption => ({ id: p.id, name: p.name, value: p.name }))
  },
}
